import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlinePhotoGalleryEditor from "@/components/admin/Content/WebPages/InlineEditors/InlinePhotoGalleryEditor";
import { useHomePageHighlights } from "@/lib/api/hooks/gallery";
import { GalleryHighlight } from "@/lib/api/types";


const MultimediaPhotos = ({ data }: { data: WebsitePageSection }) => {
 
  const { data: highlightsData, isLoading } = useHomePageHighlights('HOME_PAGE_IS_MULTIMEDIA');
  const highlightedGalleries = highlightsData?.data || [];

  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755763790/3b1a7b9af8bcf76fa2fe6ef9ea8328e56b2b5107_qetxu2.jpg";


  const formatPublishedDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <EditableSection
      sectionTag={data.sectionTag}
      contentType={data.contentType}
      data={data}
      customEditor={InlinePhotoGalleryEditor}
    >
      <div className="flex flex-col gap-4">
        
        <div className="flex flex-col gap-2">
          <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
            Photos ({highlightedGalleries.length}/4)
          </Paragraph>
          <div className="flex items-center gap-6">
            <Heading level={4}>{data.title ?? "Photo Gallery"}</Heading>
          </div>
          <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Explore our latest photo galleries.")} />
        </div>

        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <div className="col-span-4 text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#681628]"></div>
              <p className="text-sm text-gray-500 mt-2">Loading photos...</p>
            </div>
          ) : highlightedGalleries.length > 0 ? (
            highlightedGalleries.map((gallery: GalleryHighlight, index: number) => (
              <div key={gallery.galleryId} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
               
                <div className="inline-flex items-center gap-2">
                  <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                    Photo {index + 1}
                  </span>
                </div>

               
                <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                  <img
                    src={gallery.photoUrl || defaultImage}
                    alt={gallery.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src !== defaultImage) {
                        e.currentTarget.src = defaultImage;
                      }
                    }}
                  />
                </div>

                
                <div className="flex flex-col gap-1">
                  <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{gallery.title}</h4>
                  {(gallery.datePublished || gallery.publishedByName) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {gallery.datePublished && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(gallery.datePublished)}</p>
                      )}
                      {gallery.publishedByName && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {gallery.publishedByName}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-4 p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 text-sm">No gallery highlights selected. Click edit to select up to 4 galleries.</p>
            </div>
          )}
        </div>
      </div>
    </EditableSection>
  )
}

export default MultimediaPhotos